import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronRight, X } from "lucide-react";
import { NAVIGATION, type NavGroup } from "@/app/navigation";
import { usePermission } from "@/lib/permissions/useCan";
import { useSession } from "@/lib/auth/session";
import { cn } from "@/lib/utils/cn";
import { MixcoreMark } from "@/ui/brand/MixcoreMark";

type SidebarProps = {
  /** Modo desktop: colapsado (solo iconos) vs expandido (iconos + labels). */
  collapsed: boolean;
  /** Modo mobile/tablet: drawer abierto vs cerrado. */
  drawerOpen: boolean;
  /** En desktop: handler para fijar/desfijar expansión persistente. */
  onTogglePinned: () => void;
  /** En mobile/tablet: cerrar el drawer. */
  onCloseDrawer: () => void;
  /** Layout actual. */
  variant: "desktop" | "drawer";
};

const SIDEBAR_PINNED_KEY = "nx_sidebar_pinned";

export function useSidebarPin(): [boolean, () => void] {
  const [pinned, setPinned] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_PINNED_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_PINNED_KEY, pinned ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [pinned]);
  return [pinned, () => setPinned((p) => !p)];
}

export function Sidebar({
  collapsed,
  drawerOpen,
  onTogglePinned,
  onCloseDrawer,
  variant,
}: SidebarProps) {
  // Hover-to-expand: en desktop, si está colapsado y el user hace hover,
  // se expande visualmente sin pinear.
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const isDesktop = variant === "desktop";
  const visuallyExpanded = isDesktop ? !collapsed || hoverExpanded : true;

  const location = useLocation();

  // Cierra drawer al navegar en mobile.
  useEffect(() => {
    if (!isDesktop && drawerOpen) onCloseDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Drawer overlay (mobile/tablet)
  if (!isDesktop) {
    return (
      <>
        <div
          className={cn(
            "fixed inset-0 z-40 bg-black/60 transition-opacity duration-200",
            drawerOpen ? "opacity-100" : "pointer-events-none opacity-0",
          )}
          onClick={onCloseDrawer}
          aria-hidden
        />
        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-50 w-[280px] max-w-[85vw] flex flex-col",
            "bg-[var(--nx-sidebar-bg)] border-r border-[var(--nx-sidebar-border)]",
            "transition-transform duration-220 ease-nx-out",
            drawerOpen ? "translate-x-0" : "-translate-x-full",
          )}
          aria-label="Navegación principal"
        >
          <SidebarHeader expanded={true} onClose={onCloseDrawer} mobile />
          <SidebarItems expanded={true} />
        </aside>
      </>
    );
  }

  // Desktop fijo
  return (
    <aside
      onMouseEnter={() => collapsed && setHoverExpanded(true)}
      onMouseLeave={() => setHoverExpanded(false)}
      className={cn(
        "fixed inset-y-0 left-0 z-30 flex flex-col",
        "bg-[var(--nx-sidebar-bg)] border-r border-[var(--nx-sidebar-border)]",
        "transition-[width] duration-220 ease-nx-out",
        visuallyExpanded ? "w-[240px]" : "w-[64px]",
      )}
      aria-label="Navegación principal"
    >
      <SidebarHeader expanded={visuallyExpanded} onTogglePinned={onTogglePinned} pinned={!collapsed} />
      <SidebarItems expanded={visuallyExpanded} />
    </aside>
  );
}

function SidebarHeader({
  expanded,
  onTogglePinned,
  onClose,
  pinned,
  mobile,
}: {
  expanded: boolean;
  onTogglePinned?: () => void;
  onClose?: () => void;
  pinned?: boolean;
  mobile?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center h-14 px-3 border-b border-[var(--nx-sidebar-border)] shrink-0",
        expanded ? "justify-between" : "justify-center",
      )}
    >
      <div className="flex items-center gap-2 overflow-hidden">
        <MixcoreMark variant="tile" size={32} className="shrink-0" />
        {expanded && (
          <div className="flex flex-col leading-tight overflow-hidden">
            <span className="text-[13px] font-bold text-fg tracking-tight">
              MIXCORE
            </span>
            <span className="text-2xs font-semibold text-accent tracking-widest uppercase">
              Nexus
            </span>
          </div>
        )}
      </div>
      {mobile && onClose ? (
        <button
          onClick={onClose}
          className="p-1.5 rounded-md text-fg-muted hover:bg-bg-hover hover:text-fg transition-colors"
          aria-label="Cerrar menú"
        >
          <X size={18} />
        </button>
      ) : (
        expanded &&
        onTogglePinned && (
          <button
            onClick={onTogglePinned}
            className="p-1.5 rounded-md text-fg-subtle hover:bg-bg-hover hover:text-fg transition-colors"
            aria-label={pinned ? "Colapsar menú" : "Mantener menú abierto"}
            title={pinned ? "Colapsar menú" : "Mantener menú abierto"}
          >
            <ChevronRight
              size={16}
              className={cn("transition-transform duration-200", pinned && "rotate-180")}
            />
          </button>
        )
      )}
    </div>
  );
}

function SidebarItems({ expanded }: { expanded: boolean }) {
  const top = useMemo(() => NAVIGATION.filter((g) => !g.pinBottom), []);
  const bottom = useMemo(() => NAVIGATION.filter((g) => g.pinBottom), []);

  return (
    <nav className="flex-1 flex flex-col justify-between overflow-y-auto py-3">
      <ul className="flex flex-col gap-0.5 px-2">
        {top.map((g) => (
          <SidebarGroup key={g.id} group={g} expanded={expanded} />
        ))}
      </ul>
      {bottom.length > 0 && (
        <ul className="flex flex-col gap-0.5 px-2 pt-3 border-t border-[var(--nx-sidebar-border)] mt-3">
          {bottom.map((g) => (
            <SidebarGroup key={g.id} group={g} expanded={expanded} />
          ))}
        </ul>
      )}
    </nav>
  );
}

function SidebarGroup({ group, expanded }: { group: NavGroup; expanded: boolean }) {
  const allowedAny = usePermission(group.requiredPermission);
  // "Mi trabajo" exige el permiso explícito: el "*" del Owner no lo hace reclutador.
  const allowedExact = useSession((st) => !!st.user?.permissions.includes(group.requiredPermission as string));
  const allowed = group.exactPermission ? allowedExact : allowedAny;
  const location = useLocation();
  const hasChildren = !!group.children?.length;
  const isActive = location.pathname.startsWith(group.to);

  // Sub-items abiertos si el grupo está activo y el sidebar expandido.
  const showChildren = expanded && hasChildren && isActive;

  if (!allowed) return null;

  const Icon = group.icon;

  return (
    <li>
      <NavLink
        to={group.to}
        end={!hasChildren}
        className={({ isActive: navActive }) =>
          cn(
            "group flex items-center gap-3 px-2 py-2 rounded-md text-sm font-medium transition-colors",
            "text-[var(--nx-sidebar-fg)] hover:bg-bg-hover hover:text-[var(--nx-sidebar-fg-hover)]",
            (navActive || isActive) &&
              "bg-[var(--nx-sidebar-active-bg)] text-[var(--nx-sidebar-active-fg)] hover:bg-[var(--nx-sidebar-active-bg)]",
            !expanded && "justify-center",
          )
        }
        title={!expanded ? group.label : undefined}
      >
        <Icon size={18} strokeWidth={1.75} className="shrink-0" />
        {expanded && (
          <span className="flex-1 truncate">{group.label}</span>
        )}
      </NavLink>

      {showChildren && (
        <ul className="ml-7 mt-0.5 mb-1.5 flex flex-col gap-px border-l border-[var(--nx-sidebar-border)] pl-2">
          {group.children!.map((child) => (
            <SidebarChild key={child.to} item={child} />
          ))}
        </ul>
      )}
    </li>
  );
}

function SidebarChild({ item }: { item: { to: string; label: string; requiredPermission: string } }) {
  const allowed = usePermission(item.requiredPermission);
  if (!allowed) return null;
  return (
    <li>
      <NavLink
        to={item.to}
        end
        className={({ isActive }) =>
          cn(
            "block px-2 py-1.5 rounded text-[13px] transition-colors",
            "text-[var(--nx-sidebar-fg)] hover:text-[var(--nx-sidebar-fg-hover)] hover:bg-bg-hover",
            isActive && "text-[var(--nx-sidebar-active-fg)] font-medium",
          )
        }
      >
        {item.label}
      </NavLink>
    </li>
  );
}
